import React, { useRef, useState } from 'react';
import styles from './CommunitySearchBar.module.scss';
import { Container } from '@src/elements/Grid';
import {
  COLOR_LIME,
  COLOR_NEUTRAL_DARK,
  COLOR_NEUTRAL_DARKEST
} from '@src/types/colors';
import BoxIcon from '@src/elements/BoxIcon';
import { useClickOutside } from '@src/hooks/useClickOutside';
import { ButtonLinkStandard } from '../ButtonLink';
import classNames from 'classnames';
import {
  Configure,
  InstantSearch,
  SearchBox,
  useHits,
  UseHitsProps,
  useInstantSearch
} from 'react-instantsearch';
import { algoliasearch } from 'algoliasearch';
import LinkWrapper from '../LinkWrapper';
import Visibility from '@src/elements/Visibility';
import { CommunitySearchHit } from '@src/types/algolia';
import router from 'next/router';
import { MD_BREAKPOINT } from '@src/constants/breakpoints';
import { useViewport } from '@src/hooks/useViewport';

interface CommunitySearchBar {
  mapListingVariant?: boolean;
}

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID || '',
  process.env.NEXT_PUBLIC_ALGOLIA_CLIENT_API_KEY || '',
  {}
);

const CommunitySearchBar: React.FC<CommunitySearchBar> = ({
  mapListingVariant = false
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const topHitUrlRef = useRef<string | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLDivElement>(null);
  const [emptyQuery, setEmptyQuery] = useState(false);

  const { width } = useViewport();

  useClickOutside([searchRef, searchButtonRef], () => {
    setEmptyQuery(true);
    setIsSearchOpen(false);
  });

  function EmptyQueryBoundary({
    children,
    fallback
  }: {
    children: React.ReactNode;
    fallback: React.ReactNode;
  }) {
    const { indexUiState } = useInstantSearch();

    if (!indexUiState.query) {
      setEmptyQuery(true);
      return (
        <>
          {fallback}
          <div hidden>{children}</div>
        </>
      );
    }

    setEmptyQuery(false);
    return children;
  }

  function NoResultsBoundary({
    children,
    fallback
  }: {
    children: React.ReactNode;
    fallback: React.ReactNode;
  }) {
    const { results } = useInstantSearch();

    // The `__isArtificial` flag makes sure not to display the No Results message
    // when no hits have been returned.
    if (!results.__isArtificial && results.nbHits === 0) {
      return (
        <>
          {fallback}
          <div hidden>{children}</div>
        </>
      );
    }

    return children;
  }

  function CustomHits(props: UseHitsProps<CommunitySearchHit> | undefined) {
    const { items } = useHits(props);

    const communities = items
      .filter(
        (hit: CommunitySearchHit) => hit.type === 'community' && hit.published
      )
      .slice(0, 3);
    const locations = items
      .filter(
        (hit: CommunitySearchHit) => hit.type === 'location' && hit.level < 3
      )
      .slice(0, 4);
    const postcodes = items
      .filter((hit: CommunitySearchHit) => hit.type === 'postcode')
      .slice(0, 4);

    const mapHits = [...locations, ...postcodes].slice(0, 4);

    return (
      <div className={styles.searchResultsHits}>
        {items.length === 0 && (
          <div className={styles.fallback}>
            <p>No results</p>
          </div>
        )}
        <div className={styles.searchResultLocations}>
          {mapHits.length > 0 && (
            <hr className={styles.searchResultCommunitySeparator} />
          )}
          {mapHits.map((hit: CommunitySearchHit) => (
            <React.Fragment key={hit.objectID}>
              {hit.type === 'postcode' && (
                <LinkWrapper
                  href={`/find-your-community/?lat=${hit._geoloc?.lat}&lng=${hit
                    ._geoloc?.lng}&p=${btoa(
                    JSON.stringify({ locality: hit.locality, state: hit.state })
                  )}`}
                  onClick={() => {
                    setIsSearchOpen(false);
                  }}
                >
                  <article className={styles.searchResultLocation}>
                    <div className={styles.locationIcon}>
                      <BoxIcon
                        name="map"
                        size="md"
                        color={COLOR_NEUTRAL_DARK}
                      />
                    </div>
                    <p>
                      <span className={styles.locality}>{hit.locality}</span>,{' '}
                      {hit.state} {hit.postcode}
                    </p>
                  </article>
                </LinkWrapper>
              )}
              {hit.type === 'location' && (
                <LinkWrapper
                  href={`/find-your-community/${hit.slug}`}
                  onClick={() => {
                    setIsSearchOpen(false);
                  }}
                >
                  <article className={styles.searchResultLocation}>
                    <div className={styles.locationIcon}>
                      <BoxIcon
                        name="map"
                        size="md"
                        color={COLOR_NEUTRAL_DARK}
                      />
                    </div>
                    <p>
                      {hit.level === 1 && (
                        <span className={styles.locality}>{hit.state}</span>
                      )}
                      {hit.level === 2 && (
                        <>
                          <span className={styles.locality}>{hit.region}</span>
                          <span>, {hit.state}</span>
                        </>
                      )}
                      {/* {hit.level === 3 && (
                        <>
                          <span className={styles.locality}>{hit.area}</span>
                          <span>, {hit.region}</span>
                          <span>, {hit.state}</span>
                        </>
                      )}
                      {hit.level === 4 && (
                        <>
                          <span className={styles.locality}>{hit.suburb}</span>
                          <span>, {hit.region}</span>
                          <span>, {hit.state}</span>
                        </>
                      )} */}
                    </p>
                  </article>
                </LinkWrapper>
              )}
            </React.Fragment>
          ))}
        </div>
        {communities.map((hit: CommunitySearchHit) => (
          <React.Fragment key={hit.objectID}>
            <hr className={styles.searchResultCommunitySeparator} />
            <LinkWrapper
              href={`/community/${hit.slug}`}
              onClick={() => {
                setIsSearchOpen(false);
              }}
            >
              <article className={styles.searchResultCommunity}>
                <h4 className={styles.searchResultCommunityName}>{hit.name}</h4>
                <h5 className={styles.searchResultCommunityLocation}>
                  {hit.region}, {hit.stateCode} <BoxIcon name="map" size="xs" />
                </h5>
                <p className={styles.searchResultCommunityIntroText}>
                  {hit.introText}
                </p>
              </article>
            </LinkWrapper>
          </React.Fragment>
        ))}
      </div>
    );
  }

  function SearchButton() {
    const { items } = useHits();
    const { indexUiState } = useInstantSearch();

    const getSearchLink = () => {
      if (!indexUiState.query) {
        topHitUrlRef.current = '/find-your-community/';
        return `/find-your-community/`;
      }

      if (items.length > 0) {
        const community = items.find((item) => item?.type === 'community');
        const postcode = items.find((item) => item?.type === 'postcode');
        const location = items.find(
          (item) => item?.type === 'location' && item?.level < 3
        );

        const topHit = [
          ...(location ? [location] : []),
          ...(postcode ? [postcode] : []),
          ...(community ? [community] : [])
        ][0];

        if (topHit?.type === 'community') {
          topHitUrlRef.current = `/community/${topHit.slug}/`;
          return `/community/${topHit.slug}/`;
        }
        if (topHit?.type === 'location') {
          topHitUrlRef.current = `/find-your-community/${topHit.slug}`;
          return `/find-your-community/${topHit.slug}`;
        }
        if (topHit?.type === 'postcode') {
          topHitUrlRef.current = `/find-your-community/?lat=${topHit._geoloc
            ?.lat}&lng=${topHit._geoloc?.lng}&p=${btoa(
            JSON.stringify({
              locality: topHit.locality,
              state: topHit.state
            })
          )}`;
          return `/find-your-community/?lat=${topHit._geoloc?.lat}&lng=${topHit
            ._geoloc?.lng}&p=${btoa(
            JSON.stringify({
              locality: topHit.locality,
              state: topHit.state
            })
          )}`;
        }
      }
      topHitUrlRef.current = '/find-your-community/?nh=1';
      return '/find-your-community/?nh=1';
    };

    return (
      <ButtonLinkStandard
        href={getSearchLink()}
        data-testid="communitySearchBarButton"
        color={COLOR_LIME}
        aria-label="Let's go"
        size="md"
        className={styles.searchButton}
        onClick={() => {
          setIsSearchOpen(false);
        }}
      >
        Let&apos;s go
        <BoxIcon name="right-arrow-alt" size="sm" />
      </ButtonLinkStandard>
    );
  }

  function NoResults() {
    return <div className={styles.fallback}>No results</div>;
  }

  const handleEnterKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setIsSearchOpen(false);
      return router.push(topHitUrlRef.current || '/find-your-community/');
    }
  };

  return (
    <div
      className={classNames(styles.barWrapper, {
        [styles.mapListingVariant]: mapListingVariant
      })}
    >
      <Container>
        <div className={styles.barContainer}>
          <InstantSearch
            searchClient={searchClient}
            indexName={
              process.env.NEXT_PUBLIC_ALGOLIA_COMMUNITY_SEARCH_INDEX ||
              'levande_community_search_prod'
            }
            future={{
              preserveSharedStateOnUnmount: true
            }}
            routing={undefined}
          >
            <Configure analytics={true} hitsPerPage={100} />
            <div className={styles.searchBoxWrapper} ref={searchRef}>
              <div
                className={classNames(styles.searchBox, {
                  [styles.searchBoxOpen]: isSearchOpen,
                  [styles.searchBoxShadow]: !emptyQuery && isSearchOpen
                })}
              >
                <SearchBox
                  className={styles.searchBoxInput}
                  onFocus={() => setIsSearchOpen(true)}
                  onKeyDown={handleEnterKey}
                  placeholder={
                    width > MD_BREAKPOINT
                      ? 'Search by location or postcode to find your community'
                      : 'Search by location or postcode'
                  }
                />
                <BoxIcon
                  name="search"
                  color={COLOR_NEUTRAL_DARKEST}
                  className={styles.searchIcon}
                />
                <div className={styles.searchResults}>
                  <EmptyQueryBoundary fallback={null}>
                    <NoResultsBoundary fallback={<NoResults />}>
                      <CustomHits />
                    </NoResultsBoundary>
                  </EmptyQueryBoundary>
                </div>
              </div>
            </div>
            <Visibility xs={false} md={true}>
              <div ref={searchButtonRef}>
                <SearchButton />
              </div>
            </Visibility>
          </InstantSearch>
        </div>
      </Container>
    </div>
  );
};

export default CommunitySearchBar;
